import { stringToBoolean } from "@utils/string-utils";
export type MinimalUserManager = {
  user: { get: () => unknown };
  logout: () => Promise<void>;
};

export interface InactivitySessionOptions {
  /** Inactivity timeout in milliseconds */
  timeoutMs: number;
  /** Whether the inactivity logout feature is enabled */
  enabled: boolean;
  /** Optional callback invoked right before triggering logout */
  onExpire?: () => void;
}

interface InactivityController {
  start: () => void;
  stop: () => void;
  reset: () => void;
}

const DEFAULT_ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

export const resolveInactivityTimeoutMs = (): number => {
  const raw = process.env.SESSION_INACTIVITY_MS;
  if (raw) {
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }
  const rawEnv = process.env.NODE_ENV || process.env.ENVIRONMENT || "";
  const env = String(rawEnv).toLowerCase();
  if (env === "dev" || env === "development") return 10 * 60 * 1000;
  return 24 * 60 * 60 * 1000;
};

export const resolveInactivityEnabled = (): boolean => {
  const flag = process.env.SESSION_INACTIVITY_ENABLED;
  if (!flag) return false;
  return stringToBoolean(flag.trim());
};

interface InactivityInternalState {
  timer: ReturnType<typeof globalThis.setTimeout> | null;
  timeoutMs: number;
  enabled: boolean;
  onExpire?: () => void;
  userGet: () => unknown;
  logout: () => Promise<void>;
}

const handleExpiry = async (state: InactivityInternalState) => {
  if (!state.userGet()) return;
  state.onExpire?.();
  try {
    await state.logout();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[Inactivity] logout failed", e);
  }
};

const schedule = (state: InactivityInternalState) => {
  if (!state.enabled || !state.userGet()) return;
  if (state.timer) globalThis.clearTimeout(state.timer);
  state.timer = globalThis.setTimeout(() => handleExpiry(state), state.timeoutMs);
};

const registerActivityListeners = (state: InactivityInternalState, handler: () => void) => {
  if (!state.enabled || typeof globalThis.addEventListener !== "function") return;
  for (const ev of DEFAULT_ACTIVITY_EVENTS) {
    globalThis.addEventListener(ev, handler, true);
  }
};

const unregisterActivityListeners = (state: InactivityInternalState, handler: () => void) => {
  if (typeof globalThis.removeEventListener !== "function") return;
  for (const ev of DEFAULT_ACTIVITY_EVENTS) {
    globalThis.removeEventListener(ev, handler, true);
  }
};

export const attachInactivityLogout = (um: MinimalUserManager, opts?: InactivitySessionOptions): InactivityController => {
  const resolvedOpts: InactivitySessionOptions = {
    timeoutMs: resolveInactivityTimeoutMs(),
    enabled: resolveInactivityEnabled(),
  };
  if (opts) Object.assign(resolvedOpts, opts);

  const state: InactivityInternalState = {
    timer: null,
    timeoutMs: resolvedOpts.timeoutMs,
    enabled: resolvedOpts.enabled,
    onExpire: resolvedOpts.onExpire,
    userGet: () => um.user.get(),
    logout: () => um.logout(),
  };

  const activityHandler = () => schedule(state);

  const start = () => {
    registerActivityListeners(state, activityHandler);
    schedule(state);
  };
  const stop = () => {
    unregisterActivityListeners(state, activityHandler);
    if (state.timer) globalThis.clearTimeout(state.timer);
    state.timer = null;
  };
  const reset = () => schedule(state);

  return { start, stop, reset };
};
